<template>
  <VaNavbar class="public-navbar">
    <template #left>
      <RouterLink to="/" aria-label="Visit home page" class="logo-link">
        <VaIcon name="public" size="large" color="#ffffff" />
        <span class="logo-text">Happy Planet</span>
      </RouterLink>
    </template>
    <template #center>
      <nav v-if="!isMobile" class="nav-links flex gap-6">
        <VaButton preset="plain" text-color="#ffffff" to="/" :rounded="false">Home</VaButton>
        <VaButton preset="plain" text-color="#ffffff" to="/services" :rounded="false">Services</VaButton>
        <VaButton preset="plain" text-color="#ffffff" to="/pricing" :rounded="false">Pricing</VaButton>
        <VaButton preset="plain" text-color="#ffffff" to="/docs" :rounded="false">Docs</VaButton>
      </nav>
    </template>
    <template #right>
      <div v-if="!isMobile" class="auth-buttons flex">
        <template v-if="isAuthenticated">
          <VaButton preset="plain" text-color="#ffffff" to="/app/dashboard" :rounded="false">Dashboard</VaButton>
          <VaButton color="#ffffff" text-color="primary" @click="handleLogout">Logout</VaButton>
        </template>
        <template v-else>
          <VaButton color="#ffffff" text-color="primary" to="/auth/login">Login</VaButton>
          <VaButton color="#ffffff" text-color="primary" to="/auth/signup">Sign Up</VaButton>
        </template>
      </div>
      <VaButton v-else preset="plain" icon="menu" @click="toggleMobileMenu" />
    </template>
  </VaNavbar>

  <!-- Mobile Menu Sidebar -->
  <VaSidebar v-model="showMobileMenu" width="250px" position="right" class="mobile-sidebar">
    <VaList>
      <VaListItem to="/" @click="showMobileMenu = false">
        <VaListItemSection>
          <VaListItemLabel>Home</VaListItemLabel>
        </VaListItemSection>
      </VaListItem>
      <VaListItem to="/services" @click="showMobileMenu = false">
        <VaListItemSection>
          <VaListItemLabel>Services</VaListItemLabel>
        </VaListItemSection>
      </VaListItem>
      <VaListItem to="/pricing" @click="showMobileMenu = false">
        <VaListItemSection>
          <VaListItemLabel>Pricing</VaListItemLabel>
        </VaListItemSection>
      </VaListItem>
      <VaListItem to="/docs" @click="showMobileMenu = false">
        <VaListItemSection>
          <VaListItemLabel>Docs</VaListItemLabel>
        </VaListItemSection>
      </VaListItem>
      <VaDivider />
      <template v-if="isAuthenticated">
        <VaListItem to="/app/dashboard" @click="showMobileMenu = false">
          <VaListItemSection>
            <VaListItemLabel>Dashboard</VaListItemLabel>
          </VaListItemSection>
        </VaListItem>
        <VaListItem @click="handleLogout">
          <VaListItemSection>
            <VaListItemLabel>Logout</VaListItemLabel>
          </VaListItemSection>
        </VaListItem>
      </template>
      <template v-else>
        <VaListItem to="/auth/login" @click="showMobileMenu = false">
          <VaListItemSection>
            <VaListItemLabel>Login</VaListItemLabel>
          </VaListItemSection>
        </VaListItem>
        <VaListItem to="/auth/signup" @click="showMobileMenu = false">
          <VaListItemSection>
            <VaListItemLabel>Sign Up</VaListItemLabel>
          </VaListItemSection>
        </VaListItem>
      </template>
    </VaList>
  </VaSidebar>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useBreakpoint } from 'vuestic-ui'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth-store'

const breakpoint = useBreakpoint()
const router = useRouter()
const authStore = useAuthStore()

const isMobile = computed(() => breakpoint.smDown)
const isAuthenticated = computed(() => authStore.isAuthenticated)
const showMobileMenu = ref(false)

const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value
}

const handleLogout = () => {
  authStore.logout()
  showMobileMenu.value = false
  router.push('/')
}
</script>

<style lang="scss" scoped>
.public-navbar {
  background: linear-gradient(135deg, var(--va-primary) 0%, var(--va-info) 100%) !important;
  background-color: var(--va-primary) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 1000;
  padding: 0.5rem 1rem !important;
  height: 64px !important;
  min-height: 64px !important;
  max-height: 64px !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  overflow: hidden !important;

  // Make the logo white
  :deep(svg) {
    fill: white;
    color: white;
  }

  :deep(.va-navbar) {
    height: 64px !important;
    min-height: 64px !important;
    max-height: 64px !important;
  }

  :deep(.va-navbar__content) {
    padding: 0 !important;
    height: 100%;
    display: flex;
    align-items: center;
  }

  :deep(.va-navbar__background) {
    display: none;
  }

  :deep(.va-navbar__center),
  :deep(.va-navbar__left),
  :deep(.va-navbar__right) {
    display: flex;
    align-items: center;
    height: 100%;
  }
}

.logo-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: white;
  font-weight: 600;
  font-size: 1.25rem;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
}

.logo-text {
  color: white;
}

.nav-links {
  align-items: center;
  height: 100%;
}

.auth-buttons {
  align-items: center;
  gap: 0.75rem;
  height: 100%;

  // Make auth buttons have white background
  :deep(.va-button) {
    background-color: white !important;
  }
}

.mobile-sidebar {
  position: fixed !important;
}
</style>
